import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef } from 'react'
import { useToast } from '@/hooks/useToast'
import { formatPrintError } from '@/lib/api/client'
import { printApi, resolveAgentId, type PrintJob } from '@/lib/api/print'
import { useWorkerStore } from '@/stores/worker.store'

const POLL_MS = 1500
const TIMEOUT_MS = 120_000

async function waitForPrintJob(jobId: string, signal: AbortSignal): Promise<PrintJob> {
  const started = Date.now()
  while (Date.now() - started < TIMEOUT_MS) {
    if (signal.aborted) throw new Error('Отменено')
    const res = await printApi.getJob(jobId)
    const job = res.data.item
    if (job.status === 'completed') return job
    if (job.status === 'failed' || job.status === 'cancelled') {
      throw new Error(job.error_message?.trim() || 'Печать не удалась')
    }
    await new Promise(r => setTimeout(r, POLL_MS))
  }
  throw new Error('Печать не завершилась за отведённое время')
}

async function submitAndWait(
  submit: (agentId: string) => Promise<{ data: { item: PrintJob } }>,
  signal: AbortSignal,
) {
  const agentId = await resolveAgentId({ localOnly: true })
  const res = await submit(agentId)
  const jobId = res.data.item?.id
  if (!jobId) throw new Error('Не получен идентификатор задания печати')
  await waitForPrintJob(jobId, signal)
  return res
}

type BoxPrintKind = 'bundle' | 'ka'

export function usePrintBoxWithWait(taskUuid: string) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const workerName = useWorkerStore(s => s.workerName)
  const printedBy = workerName.trim() || 'worker'
  const abortRef = useRef<AbortController | null>(null)
  const inFlightRef = useRef(false)

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['task-boxes', taskUuid] })
    void qc.invalidateQueries({ queryKey: ['task-pallets', taskUuid] })
    void qc.invalidateQueries({ queryKey: ['task-km-codes', taskUuid] })
  }, [qc, taskUuid])

  const boxPrint = useMutation({
    mutationFn: async ({ boxNum, kind }: { boxNum: number; kind: BoxPrintKind }) => {
      abortRef.current?.abort()
      const ac = new AbortController()
      abortRef.current = ac
      try {
        return await submitAndWait(
          agentId =>
            kind === 'bundle'
              ? printApi.printBoxBundle(taskUuid, boxNum, { agent_id: agentId, worker_name: printedBy })
              : printApi.printBox(taskUuid, boxNum, { agent_id: agentId, worker_name: printedBy }),
          ac.signal,
        )
      } finally {
        if (abortRef.current === ac) abortRef.current = null
      }
    },
    onSuccess: (_res, { boxNum, kind }) => {
      toast.success(
        kind === 'bundle' ? `Коробка №${boxNum} напечатана` : `КА коробки №${boxNum} напечатана`,
      )
      invalidate()
    },
    onError: err => toast.error(formatPrintError(err)),
  })

  const palletKaPrint = useMutation({
    mutationFn: async ({ palletNum, copies }: { palletNum: number; copies?: number }) => {
      const agentId = await resolveAgentId({ localOnly: true })
      return printApi.printPallet(taskUuid, palletNum, {
        agent_id: agentId,
        worker_name: printedBy,
        ...(copies != null ? { copies } : {}),
      })
    },
    onSuccess: () => {
      toast.success('Задание на печать КА паллеты отправлено')
      invalidate()
    },
    onError: err => toast.error(formatPrintError(err)),
  })

  const isPrinting = boxPrint.isPending || palletKaPrint.isPending

  const printNextBox = useCallback(
    (boxNum: number) => {
      if (inFlightRef.current || boxPrint.isPending) return
      inFlightRef.current = true
      boxPrint.mutate(
        { boxNum, kind: 'bundle' },
        { onSettled: () => { inFlightRef.current = false } },
      )
    },
    [boxPrint],
  )

  const printBoxKa = useCallback(
    (boxNum: number) => {
      if (inFlightRef.current || boxPrint.isPending) return
      inFlightRef.current = true
      boxPrint.mutate(
        { boxNum, kind: 'ka' },
        { onSettled: () => { inFlightRef.current = false } },
      )
    },
    [boxPrint],
  )

  const printPalletKa = useCallback(
    (palletNum: number, opts?: { copies?: number }) => {
      if (palletKaPrint.isPending) return
      palletKaPrint.mutate({ palletNum, copies: opts?.copies })
    },
    [palletKaPrint],
  )

  return {
    printNextBox,
    printBoxKa,
    printPalletKa,
    isPrinting,
  }
}
